import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { v1 as uuidV1 } from 'uuid';
import { z } from 'zod';
import ProgressMessage from 'components/Utils/ProgressMessage';
import MenuItem from '@material-ui/core/MenuItem';
import './style.scss';
import MiscUtils from 'services/MiscUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ConfirmationDialog from 'components/Utils/ConfirmationDialog';
import UIUtils from 'services/UIUtils';
import Icon from 'components/Utils/NewIcon';
import DocumentTemplateUtils, { HealthRecordsShortcuts } from 'services/DocumentTemplateUtils';
import DocumentUtils from 'services/DocumentUtils';
import DocumentTemplateEditPanel from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateEditPanel';
import DocumentTemplateFields from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateInputs';
import SendMultipleDocuments from 'components/HealthRecords/Documents/SendMultipleDocuments';
import DefaultModal from 'components/Utils/DefaultModal';
import { FloatingField } from 'components/HealthRecords/Documents/DocumentTemplate/SelectedElementEditPanel';
import _ from 'lodash';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { uiActions } from 'careand-redux/slices/ui/ui';
import {
  MarkDocumentPendingPatientFillFillActionEnum,
  Physician,
  UserRoleEnum,
} from 'generated/api';
import { run } from 'services/FunctionUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import CPPUtils from 'services/CPPUtils';
import CalendarUtils from 'services/CalendarUtils';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import { FabricCanvas } from 'components/HealthRecords/Documents/SignDocument';
import moment from 'moment-timezone';
import { IconButton } from '@material-ui/core';
import { IsTypeNP } from 'careand-redux/selectors';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';
import { useIsLoading } from '../../../../careand-redux/slices/ui/hooks';
import { useCPPContext } from '../../../Patient/CPP/CPPContext';
import config from 'config';
import { useReduxForm, setFormValues } from 'hooks/useReduxForm';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { DefaultSwitch } from 'services/UIUtils';
import { PatientData } from '../../../../careand-redux/slices/healthRecords';
import { Device } from '../../../../services/Native/Device';

const TEMPLATE_PAGE_WIDTH = 826;
const DEFAULT_OBJECT = {} as const;

const SAVING_STATUS = {
  saving: 'saving',
  printing: 'printing',
  saving_printing: 'saving_printing',
  saving_sending: 'saving_sending',
  asking_patient: 'asking_patient',
};

// Zod schema for DocumentTemplate form
const documentTemplateSchema = z.object({
  fieldValues: z.record(z.string(), z.any()).default({}),
  editMode: z.boolean().optional(),
  templateEdit: z.any().optional(),
  selectedEditElement: z.any().optional(),
  prefill: z.record(z.string(), z.any()).optional(),
  addElement: z.string().optional(),
  copiedStyle: z.any().optional(),
  offsetX: z.number().optional(),
  offsetY: z.number().optional(),
  toggleQuickPlaceholder: z.boolean().optional(),
  numberPreviousFields: z.number().optional(),
  rowNumberFields: z.number().optional(),
  title: z.string().optional(),
  document_type: z.string().optional(),
  allow_submission: z.boolean().optional(),
  hide: z.boolean().optional(),
  pinned_to_top: z.boolean().optional(),
  hide_document: z.boolean().optional(),
  fee: z.string().optional(),
  fee_hst: z.boolean().optional(),
  fee_lines: z.any().optional(),
  metricId: z.string().optional(),
  metricValue: z.any().optional(),
  notification_text: z.string().optional(),
});

type DocumentTemplateFormValues = z.infer<typeof documentTemplateSchema>;

interface DocumentTemplateProps {
  templateId?: string;
  onSave?: (html: string, metadata: any, close?: boolean) => Promise<any>;
  onClose?: (event?: any, shouldCloseEverything?: boolean) => void;
  patient?: any;
  overrideTitle?: string;
  physician?: any;
  document?: any;
  hidePrint?: boolean;
  hideDocumentPanel?: boolean;
  hideAddPage?: boolean;
  defaultSavedDocumentCategory?: string;
  onSaveCompleted?: (result: any) => void;
  prefilledValues?: Record<string, any>;
  onChangeFields?: (event: any) => void;
  form?: string;
  getHTML?: (html: string, metadata: any) => any;
  template?: any;
  editMode?: boolean;
  pinned?: boolean;
  hideSend?: boolean;
  hideFromPatient?: boolean;
  allowSave?: boolean;
  templateOnly?: boolean;
  autoSave?: boolean;
  noZoom?: boolean;
  extraButtons?: React.ReactNode;
  forceHideButtons?: boolean;
  hidePin?: boolean;
  noteForm?: string;
  document_type?: string;
  onPinToSide?: (event: any) => void;
  hidePatientFill?: boolean;
  extraSaveData?: any;
  allow_submission?: boolean;
  documentTemplateController?: any;
  overrideId?: string;
  title?: string;
  onChangeTitle?: (title: string) => void;
}

export const DocumentTemplate = (props: DocumentTemplateProps) => {
  const dispatch = useAppDispatch();
  const {
    editMode: editModeProp,
    patient,
    pinned = false,
    hideSend = false,
    hideFromPatient,
    allowSave = !!props.patient,
    templateOnly = false,
    autoSave = false,
    noZoom = false,
    extraButtons,
    forceHideButtons,
    hideAddPage = false,
  } = props;

  // Build form name matching old redux-form pattern
  const formName = useMemo(() => {
    let form =
      props.form ||
      `DocumentTemplate-${props.templateId}-${props.overrideTitle}-${props.document?.id}-${props.overrideId}`;
    if (props.patient) form += '-' + props.patient.id;
    return form;
  }, [
    props.form,
    props.templateId,
    props.overrideTitle,
    props.document?.id,
    props.overrideId,
    props.patient,
  ]);

  const { control, watch, setValue, reset, getValues } = useReduxForm<DocumentTemplateFormValues>(
    formName,
    {
      destroyOnUnmount: false,
      // This component serves two audiences: physicians filling a template
      // (fieldValues et al — draft progress that must survive a crash or
      // closed tab) and admins editing the template layout. Only the fill
      // state persists; edit-mode state is session-only, so an interrupted
      // editing session can never silently resurface as phantom unsaved
      // changes and get saved over a live template.
      transientFields: [
        'editMode',
        'templateEdit',
        'selectedEditElement',
        'addElement',
        'copiedStyle',
        'offsetX',
        'offsetY',
        'toggleQuickPlaceholder',
        'numberPreviousFields',
        'rowNumberFields',
      ],
      defaultValues: {
        fieldValues: {},
        editMode: undefined,
        templateEdit: undefined,
        selectedEditElement: undefined,
        prefill: undefined,
        addElement: undefined,
        copiedStyle: undefined,
        offsetX: undefined,
        offsetY: undefined,
        toggleQuickPlaceholder: undefined,
        numberPreviousFields: undefined,
        rowNumberFields: undefined,
        title: undefined,
        document_type: undefined,
        allow_submission: undefined,
        hide: undefined,
        pinned_to_top: undefined,
        hide_document: undefined,
        fee: undefined,
        fee_hst: undefined,
        fee_lines: undefined,
        metricId: undefined,
        metricValue: undefined,
        notification_text: undefined,
      },
    },
  );

  // Watch all form values that were previously accessed via props
  const fieldValues = watch('fieldValues') || {};
  const editMode = watch('editMode');
  const templateEdit = watch('templateEdit');
  const selectedEditElement = watch('selectedEditElement');
  const prefill = watch('prefill');
  const addElement = watch('addElement');
  const copiedStyle = watch('copiedStyle');
  const toggleQuickPlaceholder = watch('toggleQuickPlaceholder');
  const title = watch('title');
  const document_type = watch('document_type');
  const hide = watch('hide');
  const pinned_to_top = watch('pinned_to_top');
  const fee = watch('fee');
  const fee_hst = watch('fee_hst');
  const fee_lines = watch('fee_lines');
  const metricId = watch('metricId');
  const metricValue = watch('metricValue');

  const clinic = useAppSelector((state) => state.resource.clinic);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const authentication = useAppSelector((state) => state.authentication);
  const role = authentication?.role || UserRoleEnum.Patient;
  const activeBubble = useAppSelector((state) => state.ui.activeBubble);
  const { unreactiveNoteValue } = useCPPContext();

  const isPhysician = role === 'physician';
  const isIntern = !!(authentication?.user as Physician)?.intern_of;
  const isNP = useAppSelector(IsTypeNP);

  const physician = useMemo(() => {
    if (!isPhysician || !isNP) return props.physician;
    if (isIntern) return physicians[(authentication?.user as Physician | undefined)?.intern_of!];
    return physicians[authentication?.decodedToken?.sub!];
  }, [isIntern, physicians, authentication, isNP, isPhysician, props.physician]);

  const patientHealthRecords = useAppSelector(
    (state) => state.healthRecords.byId[props.patient?.id],
  );

  const [template, setTemplate] = useState<any>();
  const [signature, setSignature] = useState<any>();
  const [error, setError] = useState<any>();
  const [saving, setSaving] = useState<string | undefined>();
  const [enableDrawing, setEnableDrawing] = useState<number | undefined>(undefined);
  const [previousElements, setPreviousElements] = useState<any[]>([]);
  const [zoomFactor, setZoomFactor] = useState(1.0);
  const [menuVisible, setMenuVisible] = useState(!pinned);
  const [sendModal, setSendModal] = useState<any>();
  const [mouseCoords, setMouseCoords] = useState<any>();
  const [askPatientDialog, setAskPatientDialog] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  const [exportData, setExportData] = useState<Record<string, any>>({});
  const [ready, setReady] = useState(false);
  const [extraPages, setExtraPages] = useState<any[]>([]);
  const [showInputDescriptions, setShowInputDescriptions] = useState(false);
  const [dragEvent, setDragEvent] = useState<any>();
  const isAutoFilling = useIsLoading(api.Templates.fillDocumentTemplate);

  // Programmatic selection (e.g. "Select All Gemini Fields" in the review
  // panel) merged with the marquee selection so bulk actions and keyboard
  // nudge work identically on both.
  const [externalSelection, setExternalSelection] = useState<Set<string>>(new Set());
  const fieldsInSelection = useMemo(() => {
    if (!dragEvent?.released) {
      return externalSelection;
    }
    const pageElem = document.querySelector(
      `.template-page[data-page-index="${dragEvent.pageIndex}"]`,
    );
    if (!pageElem) return new Set<string>();
    const pageBox = pageElem.getBoundingClientRect();
    // dragEvent coords are in the page's local (untransformed) space; client
    // rects are visual — divide by the zoom scale so both sides match.
    const scale = (pageElem as HTMLElement).offsetWidth
      ? pageBox.width / (pageElem as HTMLElement).offsetWidth
      : 1;
    const selectionBox = {
      left: dragEvent.minX,
      top: dragEvent.minY,
      bottom: dragEvent.minY + dragEvent.height,
      right: dragEvent.minX + dragEvent.width,
    };
    const allInputElements = [...pageElem.querySelectorAll('.template-field')];
    const selectedFields = allInputElements.filter((input) => {
      if (!input) {
        return;
      }
      let inputBox = input.getBoundingClientRect();
      const adjustedBox = {
        top: (inputBox.top - pageBox.top) / scale,
        left: (inputBox.left - pageBox.left) / scale,
        right: (inputBox.right - pageBox.left) / scale,
        bottom: (inputBox.bottom - pageBox.top) / scale,
      };
      return rectsIntersect(adjustedBox, selectionBox);
    });

    const marquee = selectedFields.map((input) => input.getAttribute('data-field-id') as string);
    return new Set([...marquee, ...externalSelection]);
  }, [dragEvent, externalSelection]);

  useEffect(() => {
    const className = 'field-in-selection';
    const allFields = [...document.querySelectorAll('.template-page .template-field')];

    for (let field of allFields) {
      const id = field.getAttribute('data-field-id');
      if (id && fieldsInSelection.has(id)) {
        field.classList.add(className);
      } else {
        field.classList.remove(className);
      }
    }
    // templateEdit is a dependency so the highlight survives re-renders caused
    // by moving/aligning the selected fields.
  }, [fieldsInSelection, templateEdit]);

  useEffect(() => {
    if (!editMode) return;

    const HANDLED_KEYS = ['Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];

    const cb = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement;
      if (target.tagName !== 'BODY') {
        return;
      }
      if (!HANDLED_KEYS.includes(ev.key)) {
        return;
      }
      const selectedElements = _.compact([...fieldsInSelection, selectedEditElement]);
      if (selectedElements.length === 0 || !templateEdit) {
        return;
      }
      ev.preventDefault();

      const myEdit = PerformanceUtils.clone(templateEdit);

      switch (ev.key) {
        case 'Backspace':
        case 'Delete': {
          myEdit.fields.forEach((page: any) => {
            page.inputs = page.inputs.filter((it: any) => !selectedElements.includes(it.id));
          });
          break;
        }
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight': {
          for (let page of myEdit.fields) {
            const pageHeight = page.height;
            const pageWidthVal = page.width;
            const pixelHeight = (pageHeight * TEMPLATE_PAGE_WIDTH) / pageWidthVal;
            for (let input of page.inputs) {
              if (!selectedElements.includes(input.id)) {
                continue;
              }
              const x = DocumentTemplateUtils.percentToPixel(input.x_percent, TEMPLATE_PAGE_WIDTH);
              const y = DocumentTemplateUtils.percentToPixel(input.y_percent, pixelHeight);
              const sign = ev.key === 'ArrowUp' || ev.key === 'ArrowLeft' ? -1 : 1;
              const step = ev.shiftKey ? 10 : 2;
              if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
                input.y_percent = DocumentTemplateUtils.percent(y + sign * step, pixelHeight);
              } else {
                input.x_percent = DocumentTemplateUtils.percent(
                  x + sign * step,
                  TEMPLATE_PAGE_WIDTH,
                );
              }
            }
          }
          break;
        }
      }

      setValue('templateEdit', myEdit);
    };

    window.document.addEventListener('keydown', cb);

    return () => {
      window.document.removeEventListener('keydown', cb);
    };
  }, [editMode, fieldsInSelection, selectedEditElement, templateEdit, setValue]);

  const patientSignature = fieldValues.patientSignature;
  const documentId = props.document?.id;

  const defaultExtraPages = useMemo(() => {
    if (props.document?.template_metadata) {
      const meta = JSON.parse(props.document.template_metadata || '{}');
      return meta.extra_pages;
    }
  }, [props.document]);

  useEffect(() => {
    if (defaultExtraPages) {
      setExtraPages(defaultExtraPages);
    }
  }, [defaultExtraPages]);

  useEffect(() => {
    if (documentId) {
      dispatch(api.Documents.getDocumentFile({ documentId })).then((result: any) => {
        if (result) {
          setUrl(MiscUtils.getBlobUrl(result)!);
        }
      });
    }
  }, [documentId, dispatch]);

  // No dependency array on purpose: reassigns after every commit so the
  // controller's closure always sees the latest state, without mutating a
  // prop during render.
  useEffect(() => {
    if (!props.documentTemplateController) return;
    // Clears the persisted draft (RHF + Redux) for this document. Used by the
    // patient-fill flow after a successful submit so a re-open starts clean.
    props.documentTemplateController.clearForm = () => reset();
    props.documentTemplateController.getTemplate = async () => {
      const { html, metadata } = await getHtmlAndMetadata();

      const {
        title: metaTitle,
        document_type: metaDocType = props.document_type ||
          props.defaultSavedDocumentCategory ||
          DocumentUtils.DOCUMENT_TYPE.other,
        hide: metaHide = false,
      } = metadata;

      const doc = {
        title: metaTitle || template.description,
        html,
        template_id: template.id,
        template_metadata: metadata ? JSON.stringify(metadata) : metadata,
        approved: !isIntern,
        document_type: metaDocType,
        hide: metaHide,
      };

      return doc;
    };
  });

  const description = template !== undefined ? template.description : 'Document Template';

  const htmlRef = useRef<HTMLDivElement>(null);

  const debouncedMouseMove = useCallback(_.throttle(onMouseMove, 20), []);

  const postToDate = fieldValues.post_to_date;
  const macroData = useMemo(() => {
    const data = CPPUtils.getMacroData(patientHealthRecords);
    if (postToDate) {
      return { ...data, 'D#': CalendarUtils.format(postToDate, 'YYYY/MM/DD') };
    }
    return data;
  }, [patientHealthRecords, postToDate]);

  function updatePatientSignature(sig: any) {
    const currentValues = getValues();
    const update = {
      ...(currentValues.fieldValues || {}),
      patientSignature: sig,
    };
    setValue('fieldValues', update);
    // Direct dispatch to Redux — ensures the update persists even when the
    // component is unmounted (e.g. returning from the signature canvas),
    // because the watch subscription that normally syncs RHF → Redux is gone.
    dispatch(setFormValues({ formName, values: { ...currentValues, fieldValues: update } }));
  }

  function onMouseMove(event: React.MouseEvent) {
    try {
      let coords = UIUtils.getRelativeCoordinates(event, event.currentTarget);
      setMouseCoords(coords);
    } catch (err) {}
  }

  const patientId = patient?.id;

  const loadedHr = useMemo(
    () => Object.keys(patientHealthRecords ?? {}).length > 0,
    [patientHealthRecords],
  );

  useEffect(() => {
    if (template?.pinned) {
      setValue('pinned_to_top', true);
    }
  }, [template]);

  useEffect(() => {
    if (!loadedHr && patientId) {
      dispatch(api.HealthRecords.getCPP({ patientId }));
    }
  }, [dispatch, patientId, loadedHr]);

  useEffect(() => {
    if (!!autoSave && !!ready) {
      save();
    }
  }, [autoSave, ready]);

  useEffect(() => {
    setValue('document_type', props.defaultSavedDocumentCategory);
  }, [props.defaultSavedDocumentCategory]);

  useEffect(() => {
    run(async () => {
      const tmpl =
        props.template ||
        (await dispatch(api.Templates.getDocumentTemplate({ id: props.templateId! }), {
          errorTitle: 'Document Template',
        }));
      if (tmpl) {
        let data = {
          ...tmpl,
          fields: JSON.parse(tmpl.fields),
        };

        if (!templateEdit) setValue('templateEdit', PerformanceUtils.clone(data));

        if (!title) setValue('title', props.overrideTitle || data.description);

        if (data.hide_document) setValue('hide', true);

        if (data.document_type) setValue('document_type', data.document_type);

        setTemplate(data);
      }
    }, []);
  }, []);

  useEffect(() => {
    function saveSignature(reader: FileReader) {
      reader.onloadend = () => {
        setSignature(reader.result);
      };
    }

    if (role === undefined || role === UserRoleEnum.Patient) {
      const meta = JSON.parse(props.document?.template_metadata || '{}');
      const { physician_signature } = meta;
      setSignature(physician_signature);
    } else if (physician) {
      dispatch(api.Physicians.getPhysicianSignature({ id: physician.id })).then((result: any) => {
        if (result) {
          let reader = new window.FileReader();
          reader.readAsDataURL(result);
          saveSignature(reader);
        } else {
          setError(DocumentTemplateUtils.getSignatureError());
        }
      });
    }
  }, [dispatch, isIntern, physician?.id, props.document, physician, role]);

  useEffect(() => {
    // If prefill data already exists (restored from Redux after remount, e.g. returning
    // from the signature canvas), skip re-prefilling to avoid overwriting user edits.
    if (getValues('prefill')) {
      if (!ready) setTimeout(() => setReady(true), 500);
      return;
    }

    Promise.all([
      getLifelabsContract(),
      refreshPatient(patientId, patient),
      refreshPatientHRSummary(patientId),
      refreshPatientHR(patientId),
    ])
      .then(([contract, refreshedPatient, hrSummary, healthRecords]) => {
        preFillValues(
          contract,
          refreshedPatient,
          props.prefilledValues,
          hrSummary,
          healthRecords,
          physician,
        );
      })
      .then(() => {
        if (props.document) {
          copyFieldValues(props.document);
        }
      })
      .catch(() => {
        setTimeout(() => setReady(true), 500);
      });
  }, [physician, patientId, patient, isPhysician]);

  async function deleteTemplate() {
    const result = await dispatch(api.Templates.deleteDocumentTemplate({ id: props.templateId! }), {
      title: 'Document Template',
      successText: 'Document Template deleted successfully!',
      meta: true,
    });
    // On a 409 (template still referenced by a bundle) the server error toast
    // is shown by the dispatch layer; keep the editor open so the user can act.
    if (result.isSuccess) {
      props.onClose?.();
    }
  }

  async function getLifelabsContract() {
    if (isPhysician) {
      let result = await dispatch(
        api.Resource.getSpecificOwnClinicConfiguration({
          key: 'lifelabs_contract',
        }),
      );
      return (result as any)?.value;
    }
  }

  function onPinToSide(event: any) {
    if (props.onPinToSide) return props.onPinToSide(event);
    let payload = {
      templateId: props.templateId || props.document?.template_id,
      noteForm: props.noteForm,
      document: props.document,
      title,
      overrideTitle: props.overrideTitle,
      defaultSavedDocumentCategory: props.defaultSavedDocumentCategory,
      prefilledValues: props.prefilledValues,
    };
    dispatch(
      uiActions.addPinnedDocument({
        id: props.templateId!,
        payload,
        title: payload.overrideTitle || payload.title,
        patientId: patient.id,
        type: 'template',
      }),
    );
    props.onClose?.(event, true);
  }

  function onAddNewPage(number = 1) {
    let newExtraPages: any[] = [];
    for (let i = 0; i < number; i++) {
      let fields = JSON.parse(JSON.stringify(template.fields[0]));
      fields.inputs = fields.inputs.map((input: any) => ({
        ...input,
        id: `${input.id}-${extraPages.length + i}`,
      }));
      newExtraPages.push({ image: template.images[0], fields });
    }
    setExtraPages([...extraPages, ...newExtraPages]);
  }

  function onRemovePage() {
    if (extraPages.length > 0) {
      setExtraPages(extraPages.slice(0, -1));
    }
  }

  function onAutoFill(fields: any[]) {
    if (!fields) return;

    const newFieldValues = PerformanceUtils.clone(fieldValues);

    for (let field of fields) {
      const parsedValue =
        field.value === 'true' ? 'on' : field.value === 'false' ? 'off' : field.value;
      newFieldValues[field.id] = parsedValue;
    }

    setValue('fieldValues', newFieldValues);
  }

  let chosenTemplate = editMode ? templateEdit : template;

  // Background AI field detection in flight: show a processing state and keep
  // the editor locked so nobody hand-places fields that are about to arrive.
  // Stale guard: a 'processing' flag older than 10 minutes means the pipeline
  // died mid-run — unlock rather than trapping the template forever.
  const detectionProcessing =
    chosenTemplate?.detection_status === 'processing' &&
    (chosenTemplate?.detection_started === undefined ||
      Date.now() / 1000 - chosenTemplate.detection_started < 600);

  // A template awaiting AI review opens straight into the editor's review mode
  // — the queue's whole job is that review, so don't make the admin find the
  // Edit Template button. Same gating as that button (admins on desktop,
  // opening the template itself rather than a document or fill flow).
  useEffect(() => {
    if (editMode || detectionProcessing || !template) return;
    if (physician || props.document !== undefined || allowSave || Device.isMobile()) return;
    const hasAiMarkers = (template.fields || []).some((page: any) =>
      (page.inputs || []).some((input: any) => !!input?.source),
    );
    if (hasAiMarkers) enableEditMode(true);
  }, [template, detectionProcessing]);

  let className = 'modal document-template-modal';
  if (editMode) className += ' edit-mode';
  if (noZoom) className += ' no-zoom';

  let isDrawing = enableDrawing !== undefined;

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column' }}>
      {askPatientDialog && (
        <ConfirmationDialog
          title="Ask Patient to Fill?"
          message={
            <>
              <p>The patient will receive an SMS being asked to fill the document</p>
              <p>Are you sure you want to proceed?</p>
            </>
          }
          open={askPatientDialog}
          confirmLabel="Proceed"
          onConfirm={(event: any, form: any) => {
            const input = form['Action after patient fills'];
            setAskPatientDialog(false);
            saveAndAskPatientToFill(input);
          }}
          form={[
            {
              type: 'select',
              label: 'Action after patient fills',
              inputs: Object.values(DocumentUtils.OPTIONS_FILL),
              value: DocumentUtils.OPTIONS_FILL.nothing,
            },
          ]}
          onCancel={() => setAskPatientDialog(false)}
        />
      )}
      {!!sendModal && !!patient && (
        <DefaultModal
          wrap={true}
          title="Send Documents"
          className="large-modal send-multiple-documents-modal"
          padding={false}
          content={
            <SendMultipleDocuments
              patient={patient}
              onClose={() => {
                if (props.onSaveCompleted) props.onSaveCompleted(sendModal);
                else props.onClose?.();
              }}
              documents={[sendModal]}
              /*@ts-ignore*/
              documentId={sendModal.id!}
            />
          }
          isOpen={true}
          onRequestClose={() => {
            if (props.onSaveCompleted) props.onSaveCompleted(sendModal);
            else props.onClose?.();
          }}
        />
      )}
      {!templateOnly && (
        <div className="title">
          {!!chosenTemplate && !!chosenTemplate.pdf && (
            <a href={(chosenTemplate || {}).pdf} target="_blank" rel="noopener noreferrer">
              {description + ' [pdf]'}
            </a>
          )}
          <div className="left-side-buttons" style={{ position: 'absolute', top: '5px', left: 0 }}>
            <DefaultButton
              alt="Zoom Out"
              icon="magnifying-glass-minus"
              onClick={() => setZoomFactor(Math.min(Math.max(zoomFactor - 0.1, 0.5), 2))}
              disabled={zoomFactor === 0.5 || isDrawing}
              style={{
                display: 'inline-block',
                marginLeft: '15px',
                width: '30px',
              }}
            />
            <DefaultButton
              alt="Zoom In"
              icon="magnifying-glass-plus"
              onClick={() => setZoomFactor(Math.min(Math.max(zoomFactor + 0.1, 0.5), 2))}
              disabled={zoomFactor === 2.0 || isDrawing}
              style={{
                display: 'inline-block',
                marginLeft: '15px',
                width: '30px',
              }}
            />
          </div>
          <div
            className="right-side-buttons"
            style={{
              position: 'absolute',
              top: '0',
              right: 0,
              display: 'flex',
              alignItems: 'center',
              marginRight: '10px',
            }}
          >
            {!Device.isMobile() && (
              <DefaultButton
                name={detectionProcessing ? 'Detecting Fields…' : 'Edit Template'}
                hide={!!physician || editMode || props.document !== undefined || allowSave}
                disabled={!chosenTemplate || detectionProcessing}
                onClick={() => enableEditMode(true)}
                className="blue"
                style={{ display: 'inline-block', marginRight: '15px' }}
              />
            )}
            {detectionProcessing && (
              <span
                style={{
                  fontSize: '12px',
                  color: '#9628c8',
                  marginRight: '15px',
                  alignSelf: 'center',
                }}
              >
                AI is detecting form fields — this usually takes 1–3 minutes. Reopen the template
                shortly.
              </span>
            )}
            {isPhysician && (
              <DefaultButton
                name="Pin to Side"
                icon="thumbtack"
                hide={pinned || !props.templateId || !isPhysician || props.hidePin}
                onClick={onPinToSide}
                style={{ display: 'inline-block', marginRight: '15px' }}
              />
            )}
            <DefaultButton
              name="Reset Values"
              hide={editMode || props.document !== undefined}
              disabled={!chosenTemplate}
              onClick={() => {
                setValue('fieldValues', {});
              }}
              style={{
                display: 'inline-block',
                marginRight: '15px',
                width: '140px',
              }}
            />
            {props.onClose && (
              <div>
                <IconButton onClick={props.onClose}>
                  <Icon alt="Close" src="xmark" showTooltip={true} className="close-button" />
                </IconButton>
              </div>
            )}
            <DefaultButton
              icon="bars"
              hide={!pinned}
              alt="Show/Hide Menu"
              disabled={!chosenTemplate}
              onClick={() => setMenuVisible(!menuVisible)}
              style={{
                display: 'inline-block',
                marginRight: '15px',
                width: '25px',
              }}
            />
          </div>
        </div>
      )}
      {getMainContent()}
      {!forceHideButtons &&
        !editMode &&
        (!!physician || allowSave) &&
        role !== UserRoleEnum.Patient && (
          <div className="buttons">
            <DefaultButton
              name="Cancel"
              className="red"
              style={{ marginRight: '10px', width: '90px' }}
              onClick={close}
              disabled={!!saving || !template}
              icon="circle-xmark"
            />
            <DefaultButton
              name="Save"
              style={{ marginRight: '10px', width: '90px' }}
              onClick={() => zoomOutAndDoAction(save)}
              disabled={!!saving || !template || isDrawing || !ready}
              loading={saving === SAVING_STATUS.saving}
              icon="floppy-disk"
              className="blue"
            />
            {!hideSend && (
              <DefaultButton
                name={props.document ? 'Edit and Send' : 'Save and Send'}
                style={{ marginRight: '10px', width: '150px' }}
                onClick={() => zoomOutAndDoAction(saveAndSend)}
                disabled={!!saving || !template || isDrawing || !ready}
                loading={saving === SAVING_STATUS.saving_sending}
                icon="paper-plane"
              />
            )}
            {!editMode && patient?.id && !props.hidePatientFill && (
              <DefaultButton
                name="Ask Patient to Fill"
                icon="user-pen"
                onClick={() => setAskPatientDialog(true)}
                disabled={!!saving || !template || isDrawing || !ready}
                loading={saving === SAVING_STATUS.asking_patient}
                style={{ width: 'fit-content', marginRight: '10px' }}
              />
            )}
            {config.ENVIRONMENT === 'staging' && props.patient?.id && (
              <ConfirmationButton
                name="Auto Fill"
                icon="sparkles"
                loading={isAutoFilling}
                disabled={!!saving || !template || isDrawing || !ready}
                style={{ marginRight: '10px', width: '110px' }}
                confirmation={{
                  message: 'Carey will autofill this document',
                  form: [{ type: 'textField', label: 'Instructions (optional)' }],
                }}
                onClick={(ev: any, data: any) => {
                  dispatch(
                    api.Templates.fillDocumentTemplate({
                      templateId: template.id,
                      fillDocumentTemplateParams: {
                        appointment: activeBubble?.appointment,
                        patient_id: props.patient!.id,
                        appointment_note: unreactiveNoteValue.current ?? 'No appointment note',
                        extra_instructions: data['Prompt (optional)'] ?? 'NONE',
                      },
                    }),
                  ).then(onAutoFill as any);
                }}
              />
            )}
            {!props.hidePrint && (
              <>
                <DefaultButton
                  name={props.document ? 'Edit and Print' : 'Save and Print'}
                  style={{ marginRight: '10px', width: '150px' }}
                  onClick={() => zoomOutAndDoAction(saveAndPrint)}
                  disabled={!!saving || !template || isDrawing || !ready}
                  loading={saving === SAVING_STATUS.saving_printing}
                  icon="print"
                />
                <DefaultButton
                  name="Print"
                  alt="Print"
                  icon="print"
                  style={{ width: '80px' }}
                  onClick={() => zoomOutAndDoAction(printDoc)}
                  disabled={!!saving || !template || isDrawing || !ready}
                  loading={saving === SAVING_STATUS.printing}
                />
              </>
            )}
            {extraButtons}
          </div>
        )}
    </div>
  );

  function zoomOutAndDoAction(action: () => void) {
    if (zoomFactor !== 1.0) {
      setZoomFactor(1.0);
      setTimeout(() => action(), 100);
    } else {
      action();
    }
  }

  function onCanvasLoad(canvas: any, pageSizes: any, width: number, height: number, index: number) {
    setExportData((prevState) => {
      return {
        ...prevState,
        [index]: { canvas, pageSizes, width, height },
      };
    });
  }

  function getMainContent(zoomFactorOverride?: number) {
    const dateString = moment().format('YYYY-MM');

    if (error) return error;

    let content: any = <ProgressMessage topLeft={true} />;

    // Marquee coordinates in the page's LOCAL (untransformed) space. The page
    // is zoomed via transform: scale(), so client-rect math yields visual
    // pixels — divide by the effective scale, otherwise the selection box
    // (rendered inside the scaled element) is scaled twice and lands wrong.
    const getLocalPageCoords = (ev: React.MouseEvent) => {
      const el = ev.currentTarget as HTMLElement;
      const bounds = el.getBoundingClientRect();
      const scale = el.offsetWidth ? bounds.width / el.offsetWidth : 1;
      return {
        x: (ev.clientX - bounds.left) / scale,
        y: (ev.clientY - bounds.top) / scale,
      };
    };

    const onMouseHold = (ev: React.MouseEvent, pageIndex: number) => {
      if (!editMode) {
        return;
      }
      const coords = getLocalPageCoords(ev);
      setDragEvent({ from: coords, pageIndex });
    };

    const onMouseRelease = (ev: React.MouseEvent) => {
      if (!editMode) {
        return;
      }
      const coords = getLocalPageCoords(ev);

      if (dragEvent) {
        const width = Math.abs(dragEvent.from.x - coords.x);
        const height = Math.abs(dragEvent.from.y - coords.y);
        if (width < 10 && height < 10) {
          setDragEvent(undefined);
          setValue('selectedEditElement', null);
        }
      }

      setDragEvent((prev: any) => {
        if (!prev) {
          return;
        }
        const width = Math.abs(prev.from.x - coords.x);
        const height = Math.abs(prev.from.y - coords.y);
        if (width < 10 && height < 10) {
          return;
        }
        setValue('selectedEditElement', null);
        const minX = Math.min(prev.from.x, coords.x);
        const minY = Math.min(prev.from.y, coords.y);
        return { ...prev, to: coords, released: true, minX, minY, width, height };
      });
    };

    const onMouseMoveHandler = (ev: React.MouseEvent) => {
      if (!editMode) {
        return;
      }
      if (!dragEvent || dragEvent.released) {
        return;
      }
      const coords = getLocalPageCoords(ev);
      setDragEvent((prev: any) => prev && !prev.released && { ...prev, to: coords });
    };

    if (template !== undefined) {
      const { fields, images } = editMode ? templateEdit : template;

      content = [];

      let zoom = zoomFactorOverride || zoomFactor;

      let allImages = [...images, ...(extraPages?.map((page: any) => page.image) || [])];
      let allFields = [...fields, ...(extraPages?.map((page: any) => page.fields) || [])];

      allImages.forEach((image: string, index: number) => {
        let page = allFields[index] || {};
        let { height, width, inputs = [] } = page;
        let templateHeight = (height * TEMPLATE_PAGE_WIDTH) / width;

        let isDrawingPage = enableDrawing === index;
        let svgThumbnails = getSvgsThumbnails(index);

        let zoomMarginTop = zoom > 1.0 ? (zoomFactor - 1.0) * 10 * 60 : 0;

        if (index >= 1) zoomMarginTop *= 2;

        let zoomStr = zoomFactor.toFixed(1);
        let zoomMarginStr = zoomMarginTop.toFixed(1);

        let zoomStyle = {
          transform: `scale(${zoomStr})`,
          marginTop: `${zoomMarginStr}px`,
        };

        content.push(
          <div
            className="template-page"
            data-force-theme="light"
            data-page-index={index}
            data-role={role}
            onMouseDown={(ev) => onMouseHold(ev, index)}
            onMouseUp={onMouseRelease}
            onMouseMove={onMouseMoveHandler}
            ref={(ref) => {
              if (ref && pageWidth !== ref.clientWidth) {
                setPageWidth(ref.clientWidth);
              }
            }}
            style={{
              position: 'relative',
              width: TEMPLATE_PAGE_WIDTH + 'px',
              height: templateHeight,
              maxHeight: '29.7cm',
              ...zoomStyle,
            }}
            key={'page-' + index}
          >
            {editMode && dragEvent && (
              <SelectionBox dragEvent={dragEvent} setDragEvent={setDragEvent} />
            )}
            {!editMode && (
              <>
                {url && role !== UserRoleEnum.Patient && (
                  <div className="drawing-buttons">
                    <div className="toggle-drawing box-shadow" onClick={() => toggleDrawing(index)}>
                      <Icon src="pen-line" width="20px" className="white" alt="Toggle Drawing" />
                    </div>
                    <div className="thumbnails">{svgThumbnails}</div>
                  </div>
                )}
                {isDrawingPage && (
                  /*@ts-ignore*/
                  <FabricCanvas
                    key={index}
                    signature={signature}
                    allowZoom={false}
                    url={url}
                    width={pageWidth}
                    patient={patient}
                    physician={physician}
                    clinic={clinic}
                    onLoad={(canvas: any, pageSizes: any, width: number, height: number) => {
                      onCanvasLoad(canvas, pageSizes, width, height, index);
                    }}
                    pageNumber={index}
                    macroData={macroData}
                    pdfScale={1.399}
                  />
                )}
              </>
            )}
            <DocumentTemplateFields
              inputs={inputs}
              pageWidth={TEMPLATE_PAGE_WIDTH}
              pageHeight={templateHeight}
              index={index}
              fieldValues={fieldValues}
              editMode={editMode}
              prefill={prefill}
              selectedEditElement={selectedEditElement}
              signatureUrl={signature}
              updateField={updateField}
              selectEditElement={onClickElement}
              onChange={onChangeHandler}
              change={(field: string, value: any) => setValue(field as any, value)}
              toggleQuickPlaceholder={toggleQuickPlaceholder}
              patientSignature={patientSignature}
              setPatientSignature={updatePatientSignature}
              role={role}
              macroData={macroData}
              showInputDescriptions={showInputDescriptions}
              fieldsInSelection={fieldsInSelection}
            />
            <img
              key="images"
              id={'image' + index}
              alt={description}
              src={image + `?${dateString}`}
              style={{
                width: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'block',
              }}
              draggable={false}
              onMouseLeave={() => {
                setMouseCoords(undefined);
                setTimeout(() => {
                  setMouseCoords(undefined);
                }, 30);
              }}
              onMouseMove={(event) => (editMode ? debouncedMouseMove(event) : undefined)}
              onClick={(event) => {
                let coords = UIUtils.getRelativeCoordinates(event, event.target);
                addNewElement(index, coords.x!, coords.y!);
              }}
            />
            <FloatingField
              pageWidth={TEMPLATE_PAGE_WIDTH}
              pageHeight={templateHeight}
              coords={mouseCoords}
              pageNumber={index}
              prepareNewElement={prepareNewElement}
              prefill={prefill}
            />
          </div>,
        );
      });

      // Calculate margin for button to account for zoom
      const buttonMarginTop = zoom > 1.0 ? (zoom - 1.0) * 10 * 60 : 0;

      content.push(
        !hideAddPage && template?.images?.length === 1 && (
          <div
            className="py-4 flex justify-center gap-x-3 add-page-button"
            style={{ marginTop: `${buttonMarginTop}px` }}
            key="add-page-button"
          >
            <>
              <DefaultButton
                icon="circle-plus"
                name="Add New Page"
                className="white my-3"
                onClick={() => onAddNewPage()}
                style={{ alignSelf: 'center' }}
              />
              {extraPages.length > 0 && (
                <DefaultButton
                  icon="trash"
                  name="Remove Page"
                  className="red my-3"
                  onClick={() => onRemovePage()}
                  style={{ alignSelf: 'center' }}
                />
              )}
            </>
          </div>
        ),
      );
    }

    return (
      <div className="content" style={{ flexGrow: '1' }}>
        <div className="pages-wrapper scroll-list" ref={htmlRef}>
          {content}
        </div>
        {!props.hideDocumentPanel && menuVisible && (
          <div className="panels-wrapper scroll-list">
            {editMode && (
              <DocumentTemplateEditPanel
                control={control}
                editMode={editMode}
                templateEdit={templateEdit}
                selectedEditElement={selectedEditElement}
                addElement={addElement}
                copiedStyle={copiedStyle}
                toggleQuickPlaceholder={toggleQuickPlaceholder}
                change={(field: string, value: any) => setValue(field as any, value)}
                onClose={() => enableEditMode(false)}
                deleteTemplate={deleteTemplate}
                template={template}
                updateField={updateField}
                setTemplate={setTemplate}
                selectEditElement={onClickElement}
                addMultipleNewElements={addMultipleNewElements}
                setPreviousElements={setPreviousElements}
                previousElements={previousElements}
                pageWidth={TEMPLATE_PAGE_WIDTH}
                changeElements={changeElements}
                showInputDescriptions={showInputDescriptions}
                setShowInputDescriptions={setShowInputDescriptions}
                fieldsInSelection={fieldsInSelection}
                setExternalSelection={setExternalSelection}
                clearSelection={() => {
                  setDragEvent(undefined);
                  setExternalSelection(new Set());
                }}
              />
            )}
            {getDocumentDetailsPanel()}
          </div>
        )}
      </div>
    );
  }

  function copyFieldValues(doc: any) {
    if (doc && doc.template_metadata) {
      const metadata = JSON.parse(doc.template_metadata);
      let newFieldValues = {
        ...fieldValues,
        ...metadata,
      };
      if (patientSignature) {
        newFieldValues.patientSignature = patientSignature;
      }
      setValue('fieldValues', newFieldValues);
    }
  }

  async function toggleDrawing(index: number) {
    if (enableDrawing !== undefined) {
      await saveSvg(enableDrawing);
    }
    let newValue = enableDrawing === index ? undefined : index;
    setEnableDrawing(newValue);
    setZoomFactor(1.0);
  }

  async function saveSvg(index: number) {
    return new Promise<void>((resolve) => {
      if (!exportData[index]) return;
      const { canvas, pageSizes } = exportData[index];

      canvas.clone((newCanvas: any) => {
        let originalObjects = newCanvas.getObjects();

        const pageSizesCopy = PerformanceUtils.clone(pageSizes);

        const pages = originalObjects.slice(0, pageSizesCopy.length);
        const otherObjects = originalObjects.slice(pageSizesCopy.length);

        let top = 0;
        let shouldExport = false;

        pages.forEach((page: any, pageIndex: number) => {
          pageSizesCopy[pageIndex].top = top;
          top += pageSizesCopy[pageIndex].height;
          otherObjects.forEach((object: any) => {
            if (page.intersectsWithObject(object)) {
              shouldExport = true;
              pageSizesCopy[pageIndex].index = pageIndex;
            }
          });
        });

        if (shouldExport) {
          pages.forEach((object: any) => newCanvas.remove(object));
          newCanvas.requestRenderAll();
          addSvg(index, newCanvas.toSVG());
        }
        resolve();
      });
    });
  }

  function addSvg(index: number, svg: string) {
    let newFieldValues = PerformanceUtils.clone(fieldValues);

    const svgValues = newFieldValues.svg || {};
    const svgValuesIndex = svgValues[index] || [];

    newFieldValues = {
      ...newFieldValues,
      svg: {
        ...svgValues,
        [index]: [...svgValuesIndex, { id: uuidV1(), type: 'svg', svg }],
      },
    };

    setValue('fieldValues', newFieldValues);
    setEnableDrawing(undefined);
  }

  function removeSvg(index: number, id: string) {
    let newFieldValues = PerformanceUtils.clone(fieldValues);

    let svgValues = newFieldValues.svg || {};
    let svgValuesIndex = svgValues[index] || [];

    let changedSvgs = svgValuesIndex.filter((svg: any) => svg.id !== id);

    newFieldValues = {
      ...newFieldValues,
      svg: {
        ...svgValues,
        [index]: changedSvgs,
      },
    };

    setValue('fieldValues', newFieldValues);
  }

  function updateField(id: string, changes: Record<string, any>) {
    let newTemplateEdit = PerformanceUtils.clone(templateEdit);
    let data = newTemplateEdit.fields;
    let found = false;
    data.forEach((page: any) => {
      if (!found) {
        page.inputs.forEach((item: any) => {
          if (item.id === id) {
            found = true;

            let { height, width } = page;

            let pixelHeight = (height * TEMPLATE_PAGE_WIDTH) / width;

            if (changes.x) {
              item.x_percent = DocumentTemplateUtils.percent(changes.x, TEMPLATE_PAGE_WIDTH);
              delete changes.x;
            }

            if (changes.y) {
              item.y_percent = DocumentTemplateUtils.percent(changes.y, pixelHeight);
              delete changes.y;
            }

            if (changes.w) {
              item.w_percent = DocumentTemplateUtils.percent(changes.w, TEMPLATE_PAGE_WIDTH);
              delete changes.w;
            }

            if (changes.h) {
              item.h_percent = DocumentTemplateUtils.percent(changes.h, pixelHeight);
              delete changes.h;
            }
            for (let key in changes) {
              item[key] = changes[key];
            }
          }
        });
      }
    });
    setValue('templateEdit', newTemplateEdit);
  }

  function prepareNewElement(
    pageNumber: number,
    x: number,
    y: number,
    elementData: any = {},
    fromClick = true,
  ) {
    let copiedStyleVal = PerformanceUtils.clone(copiedStyle);

    if (!editMode || !addElement) return false;

    if (fromClick && addElement === 'checkbox') {
      y -= 7;
      x -= 5;
    }

    if (fromClick && addElement === 'textfield') {
      y -= 13;
    }

    let element = PerformanceUtils.clone({
      ...elementData,
      type: elementData.type || addElement,
    });

    element.id = element.id || uuidV1();

    element.w_percent = element.w_percent || '15%';
    element.h_percent = element.h_percent || '1.2%';

    if (fromClick && addElement === 'patient signature') {
      element.h_percent = '3%';
    }

    // Read-only access to the page dimensions; the returned element never
    // aliases templateEdit, so no clone is needed here.
    let page = templateEdit.fields[pageNumber];
    let { height, width } = page;
    let pixelHeight = (height * TEMPLATE_PAGE_WIDTH) / width;

    element.x_percent = '0%';
    element.y_percent = '0%';

    let x_percent_change = DocumentTemplateUtils.percent(x, TEMPLATE_PAGE_WIDTH);
    element.x_percent = DocumentTemplateUtils.addPercentages(element.x_percent, x_percent_change);

    let y_percent_change = DocumentTemplateUtils.percent(y, pixelHeight);
    element.y_percent = DocumentTemplateUtils.addPercentages(element.y_percent, y_percent_change);

    if (fromClick && copiedStyleVal !== undefined) {
      if (copiedStyleVal.type !== element.type) {
        delete copiedStyleVal['h_percent'];
      }

      element = {
        ...element,
        ...copiedStyleVal,
        type: element.type,
      };
    }
    return element;
  }

  function changeElements(elements: any[], pageNumber: number) {
    let newTemplateEdit = PerformanceUtils.clone(templateEdit);

    newTemplateEdit.fields[pageNumber].inputs = newTemplateEdit.fields[pageNumber].inputs.map(
      (input: any) => {
        let found = elements.find((element) => element.id === input.id);
        if (found) return found;
        return input;
      },
    );

    setValue('templateEdit', newTemplateEdit);
  }

  function addMultipleNewElements(pageNumber: number, elements: any[], fromClick = false) {
    elements = elements.map((element) =>
      prepareNewElement(pageNumber, element.x, element.y, element, fromClick),
    );

    if (elements.length > 0) {
      let newTemplateEdit = PerformanceUtils.clone(templateEdit);

      newTemplateEdit.fields[pageNumber].inputs = [
        ...newTemplateEdit.fields[pageNumber].inputs,
        ...elements,
      ];

      setValue('templateEdit', newTemplateEdit);
      setValue('selectedEditElement', elements[elements.length - 1].id);

      let newPreviousElements = [
        ...previousElements,
        ...elements.map((element) => {
          return {
            pageNumber,
            element,
          };
        }),
      ];

      setPreviousElements(newPreviousElements);
    }
  }

  function addNewElement(
    pageNumber: number,
    x: number,
    y: number,
    elementData: any = {},
    fromClick = true,
  ) {
    elementData.x = x;
    elementData.y = y;
    return addMultipleNewElements(pageNumber, [elementData], fromClick);
  }

  function getDocumentDetailsPanel() {
    if (editMode || !template || props.document || !physician) return null;

    let panelClassName = 'document-details-panel padded-box full-height';

    const onChangeDate = (_event: any, date: number) => {
      const newFieldValues = PerformanceUtils.clone(fieldValues);
      newFieldValues.post_to_date = date;
      setValue('fieldValues', newFieldValues);
    };

    const onChangeNotificationText = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newFieldValues = PerformanceUtils.clone(fieldValues);
      newFieldValues.notification_text = event.target.value;
      setValue('fieldValues', newFieldValues);
    };

    const willReceiveNotification =
      fieldValues.post_to_date &&
      moment.unix(fieldValues.post_to_date).isAfter(moment().add(1, 'week'));

    return (
      <div className={panelClassName}>
        <div className="document-details-content">
          <span className="small-title">Document Details</span>
          <DefaultTextField name="title" control={control} label="Title" disabled={!!saving} />
          <DefaultSelectField
            name="document_type"
            control={control}
            label="Document Type"
            disabled={!!saving}
            inputElements={Object.keys(DocumentUtils.DOCUMENT_TYPE).map((key) => {
              return (
                <MenuItem
                  id={'menu-item-' + key}
                  value={(DocumentUtils.DOCUMENT_TYPE as any)[key]}
                  key={key}
                >
                  {(DocumentUtils.DOCUMENT_TYPE as any)[key]}
                </MenuItem>
              );
            })}
            style={{ width: '100%' }}
          />
          <DefaultSwitch
            name="hide"
            control={control}
            label="Hide"
            disabled={!!saving}
            reversed={true}
          />
          <DefaultSwitch
            name="pinned_to_top"
            control={control}
            label="Pin to top of CPP"
            disabled={!!saving}
            reversed={true}
          />
          <DefaultDatePicker
            name="post_to_date"
            value={fieldValues.post_to_date}
            className="post-to-date-field"
            label="Post to Date"
            onChange={onChangeDate}
          />
          {willReceiveNotification && (
            <DefaultTextField
              name="notification_text"
              control={control}
              label="Patient Notification"
              style={{ width: '100%' }}
              placeholder="Your health records have been updated"
              multiline={true}
              inputLabelProps={{ shrink: true }}
            />
          )}
          <div className="mt-4">
            <HealthRecordsShortcuts />
          </div>
        </div>
      </div>
    );
  }

  async function printDoc() {
    setSaving(SAVING_STATUS.printing);
    let { metadata, html } = await getHtmlAndMetadata();
    await printHTML(html, metadata);
    setSaving(undefined);
  }

  async function saveAndSend() {
    let doc = await save(SAVING_STATUS.saving_sending, false);
    if (!doc) {
      MiscUtils.showToastrError(
        'Send Documents',
        'The document could not be saved, so it was not sent',
      );
      return;
    }
    setSendModal(doc);
  }

  async function saveAndPrint() {
    let doc = await save(SAVING_STATUS.saving_printing);
    if (!doc) {
      MiscUtils.showToastrError(
        'Print Document',
        'The document could not be saved, so it was not printed',
      );
      return;
    }
    await printDocument(doc);
  }

  async function save(status = SAVING_STATUS.saving, shouldClose = true) {
    if (!ready) return;
    setSaving(status);
    try {
      let { metadata, html } = await getHtmlAndMetadata();
      if (props.getHTML) {
        return await props.getHTML(html, metadata);
      }

      let doc;
      if (!!props.onSave) {
        doc = await props.onSave(html, metadata, shouldClose);
        reset();
      } else {
        doc = await onSaveDefault(html, metadata, shouldClose);
      }
      return doc;
    } finally {
      setSaving(undefined);
    }
  }

  async function saveAndAskPatientToFill(input: string) {
    let fillAction = Object.keys(DocumentUtils.OPTIONS_FILL).find(
      (key) => (DocumentUtils.OPTIONS_FILL as any)[key] === input,
    ) as MarkDocumentPendingPatientFillFillActionEnum;

    const doc = await save(SAVING_STATUS.saving, false);
    if (!doc?.id) {
      MiscUtils.showToastrError(
        'Ask Patient to Fill',
        'The document could not be saved, so the patient was not asked to fill it',
      );
      return;
    }
    setSaving(SAVING_STATUS.asking_patient);
    try {
      const asked = await dispatch(
        api.Documents.markDocumentPendingPatientFill({
          patientId: patient.id,
          documentId: doc.id,
          fillAction,
        }),
        { errorTitle: 'Ask Patient to Fill' },
      );
      return asked;
    } finally {
      setSaving(undefined);
      close();
    }
  }

  async function getHtmlAndMetadata() {
    let htmlContent = htmlRef.current!.outerHTML;

    htmlContent = htmlContent.replace(/value="checked"/g, 'checked');

    let zoomStr = zoomFactor.toFixed(1);

    htmlContent = htmlContent.split(`transform: scale(${zoomStr});`).join('');

    let div = document.createElement('div');
    div.innerHTML = htmlContent;

    let pages = div.children[0]!.children;
    for (let i = 0; i < pages.length; i++) {
      let page = pages[i]!;
      if (!page.className?.includes('add-page-button')) {
        let childClassName = page.children[0]?.className;
        if (childClassName?.includes('drawing-buttons')) {
          page.children[0]!.remove();
        }
      } else {
        page.remove();
      }
    }

    let html = '<html><head><meta charset="UTF-8"></head><body>' + div.outerHTML + '</body></html>';

    let metadata = getMetadata();
    return { html, metadata };
  }

  async function printDocument(doc: any, buffer?: any) {
    if (!buffer) {
      if (!doc) return;
      buffer = await dispatch(api.Documents.getDocumentFile({ documentId: doc.id }));
    }
    let blobUrl = MiscUtils.getBlobUrl(buffer);
    if (blobUrl) {
      MiscUtils.printUrl(blobUrl);
    }
  }

  async function printHTML(html: string, metadata: any) {
    let {
      title: metaTitle,
      document_type: metaDocType = props.document_type ||
        props.defaultSavedDocumentCategory ||
        DocumentUtils.DOCUMENT_TYPE.other,
      hide: metaHide = false,
      pinned: metaPinned = false,
    } = metadata;
    let documentTemplateSubmission = {
      title: metaTitle || template.description,
      html,
      template_id: template.id,
      template_metadata: metadata ? JSON.stringify(metadata) : metadata,
      approved: !isIntern,
      document_type: metaDocType,
      hide: metaHide,
      pinned: metaPinned,
    };

    let result = await dispatch(
      api.Documents.previewDocumentFromHtml({
        documentTemplateSubmission,
        patientId: props.patient!.id,
      }),
      {
        errorTitle: 'Preview Document',
      },
    );

    if (result) {
      await printDocument(undefined, result);
    }
  }

  async function onSaveDefault(html: string, metadata: any, shouldClose = true) {
    let {
      title: metaTitle,
      document_type: metaDocType = props.document_type ||
        props.defaultSavedDocumentCategory ||
        DocumentUtils.DOCUMENT_TYPE.other,
      hide: metaHide = false,
    } = metadata;

    let documentTemplateSubmission = {
      title: metaTitle || template.description,
      html,
      physician_id: physician?.id,
      template_id: template.id,
      template_metadata: metadata ? JSON.stringify(metadata) : metadata,
      approved: !isIntern,
      document_type: metaDocType,
      repeat_options: props.extraSaveData?.repeat_options,
      date: props.extraSaveData?.date,
      notification_text: props.extraSaveData?.notification_text,
      hide: hideFromPatient !== undefined ? hideFromPatient : metaHide,
      allow_submission: props.allow_submission,
      pinned: pinned_to_top,
    };

    let result = await dispatch(
      api.Documents.addDocumentFromHtml({
        documentTemplateSubmission,
        patientId: props.patient!.id,
      }),
      {
        title: 'Add Document',
        successText: 'Document added successfully!',
      },
    );

    if (result) {
      if (shouldClose && props.onSaveCompleted) props.onSaveCompleted(result);
      reset();
      return result;
    }
  }

  function getMetadata() {
    let { fields, images } = template;

    let metadata: Record<string, any> = {
      title,
      hide,
      document_type,
      fee: fee ? parseFloat(fee.split('$').join('')) : undefined,
      patientSignature,
      physician_signature: editMode ? undefined : signature,
      fee_hst,
      fee_lines,
      metricId,
      metricValue,
    };

    metadata.post_to_date = fieldValues?.post_to_date;
    metadata.notification_text = fieldValues?.notification_text;

    let allImages = [...images, ...(extraPages?.map((page: any) => page.image) || [])];
    let allFields = [...fields, ...(extraPages?.map((page: any) => page.fields) || [])];

    allImages.forEach((_image: string, index: number) => {
      let page = allFields[index];
      let { inputs } = page;

      inputs.forEach((input: any) => {
        let value = undefined;
        let { id, placeholder } = input;

        if (fieldValues[id] !== undefined) {
          if (input.type === 'checkbox')
            value = fieldValues[id] === true || fieldValues[id] === 'on';
          else value = fieldValues[id];
        } else if (placeholder && prefill && prefill[placeholder] !== undefined) {
          if (input.type === 'checkbox')
            value = prefill[placeholder] === true || prefill[placeholder] === 'on';
          else value = prefill[placeholder];
        } else if (prefill && prefill[id] !== undefined) {
          if (input.type === 'checkbox') value = prefill[id] === true || prefill[id] === 'on';
          else value = prefill[id];
        }

        if (value !== undefined) {
          metadata[id] = value;
          if (placeholder) {
            metadata[`${id}.placeholder`] = DocumentTemplateUtils.getPlaceholderLabel(placeholder);
          }
        }
      });
    });

    if (extraPages.length > 0) {
      if (!metadata) metadata = {};
      metadata.extra_pages = extraPages;
    }

    return metadata;
  }

  function getSvgsThumbnails(index: number) {
    let inputs = (fieldValues.svg || {})[index] || [];

    return inputs
      .filter((input: any) => input.type === 'svg')
      .map((field: any) => (
        <div
          key={`thumbnail-${field.id}`}
          className="thumbnail-wrapper"
          onClick={() => removeSvg(index, field.id)}
        >
          <Icon src="trash" className="red" fontAwesomeStyle="fas" />
          <span
            className="svg-thumbnail"
            dangerouslySetInnerHTML={{ __html: DocumentTemplateUtils.sanitizeSvg(field.svg) }}
          />
        </div>
      ));
  }

  async function refreshPatient(pid: string | undefined, defaultPatient: any = undefined) {
    let chosenPatient;
    if (!pid) {
      if (defaultPatient) {
        chosenPatient = defaultPatient;
      } else {
        chosenPatient = DocumentTemplateUtils.getDummyPatient();
      }
    } else {
      let result = defaultPatient || (await dispatch(api.Patients.getPatient({ id: pid })));
      if (result) chosenPatient = result;
    }
    return chosenPatient;
  }

  async function refreshPatientHRSummary(pid: string | undefined) {
    if (!pid) {
      return;
    }
    return dispatch(api.HealthRecords.getHealthDataSummary({ patientId: pid }));
  }

  async function refreshPatientHR(pid: string | undefined) {
    if (!pid) {
      return;
    }
    return dispatch(api.HealthRecords.getHealthRecords({ patientId: pid }));
  }

  async function preFillValues(
    lifelabsContract: any,
    patientData: any,
    prefilledValues: any,
    hrSummary: any,
    healthRecords: any,
    chosenPhysician: any,
  ) {
    if (!chosenPhysician) chosenPhysician = DocumentTemplateUtils.getDummyPhysician();

    let data = DocumentTemplateUtils.getPrefill(
      patientData,
      chosenPhysician,
      clinic,
      lifelabsContract,
      prefilledValues,
      hrSummary,
      healthRecords,
    );

    let events = Object.keys(data).map((name) => {
      let value: any = (data as any)[name]!;
      if (value === true) value = 'on';
      if (value === false) value = 'off';
      return {
        target: {
          name,
          value,
        },
      };
    });

    onChangeMultiple(events);
    setValue('prefill', data);
    setTimeout(() => {
      setReady(true);
    }, 500);
  }

  function onClickElement(id: string, ev?: React.MouseEvent) {
    ev?.stopPropagation();
    setDragEvent(undefined);
    setValue('selectedEditElement', id);
  }

  function onChangeMultiple(events: Array<{ target: { name: string; value: any } }>) {
    let newFieldValues = PerformanceUtils.clone(fieldValues);
    events.forEach((event) => {
      newFieldValues = {
        ...newFieldValues,
        [event.target.name]: event.target.value,
      };
    });
    setValue('fieldValues', newFieldValues);
  }

  function onChangeHandler(event: any) {
    if (editMode) {
      if (event.preventDefault) event.preventDefault();
      if (event.stopPropagation) event.stopPropagation();
      return false;
    }

    let newFieldValues = PerformanceUtils.clone(fieldValues);

    newFieldValues = {
      ...newFieldValues,
      [event.target.name]: event.target.value,
    };
    setValue('fieldValues', newFieldValues);
    if (props.onChangeFields) props.onChangeFields(event);
  }

  function enableEditMode(val: boolean) {
    setValue('editMode', val);
    setValue('templateEdit', PerformanceUtils.clone(template));
    setValue('selectedEditElement', null);
  }

  function close() {
    reset();
    props.onClose?.();
  }
};

function SelectionBox({
  dragEvent,
  setDragEvent,
}: {
  dragEvent: any;
  setDragEvent: (v: any) => void;
}) {
  const selectionWidth = Math.abs(dragEvent?.from?.x - dragEvent?.to?.x);
  const selectionHeight = Math.abs(dragEvent?.from.y - dragEvent?.to?.y);
  const [minX, minY] = [
    Math.min(dragEvent?.from?.x, dragEvent?.to?.x),
    Math.min(dragEvent?.from?.y, dragEvent?.to?.y),
  ];

  const style = {
    transform: `translate(${minX}px, ${minY}px)`,
    width: `${selectionWidth}px`,
    height: `${selectionHeight}px`,
  };

  if (!dragEvent || !dragEvent.to) {
    return null;
  }

  return <>{!dragEvent.released && <div className="selection-box" style={style}></div>}</>;
}

function rectsIntersect(rect1: any, rect2: any) {
  return !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );
}

export default DocumentTemplate;
